import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Account } from "../../../../api/src/accounts/accounts.service";
import type { Model } from "../../../../api/src/models/models.service";
import { displayAccountName, noModelLabel } from "../../lib/display";

const INITIAL_DISPLAY_COUNT = 2;

type AccountModelSummaryProps = {
  accounts: Account[];
  models: Model[];
};

export default function AccountModelSummary({
  accounts,
  models,
}: AccountModelSummaryProps) {
  const [expanded, setExpanded] = useState(false);

  const modelMap = useMemo(() => {
    return new Map(models.map((m) => [m.id, m.name]));
  }, [models]);

  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) =>
      displayAccountName(a.displayName, a.displayNumber).localeCompare(
        displayAccountName(b.displayName, b.displayNumber),
      ),
    );
  }, [accounts]);

  if (accounts.length === 0) {
    return null;
  }

  const displayedAccounts = expanded
    ? sortedAccounts
    : sortedAccounts.slice(0, INITIAL_DISPLAY_COUNT);

  const remainingCount = sortedAccounts.length - INITIAL_DISPLAY_COUNT;
  const showToggle = sortedAccounts.length > INITIAL_DISPLAY_COUNT;

  return (
    <div className="mb-3 ms-0">
      <table className="table table-sm table-borderless mb-1">
        <thead>
          <tr>
            <th className="px-1 py-1" style={{ width: "50%" }}>
              Account
            </th>
            <th className="px-1 py-1">Assigned Model</th>
          </tr>
        </thead>
        <tbody>
          {displayedAccounts.map((account) => (
            <tr key={account.id}>
              <td className="ps-0">
                <Link to={`/clients/accounts/${account.id}/model-trading`}>
                  {displayAccountName(
                    account.displayName,
                    account.displayNumber,
                  )}
                </Link>
              </td>
              <td>
                {account.assignedModelId
                  ? (modelMap.get(account.assignedModelId) ?? noModelLabel)
                  : noModelLabel}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showToggle && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 text-decoration-none"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "▲ Show less" : `▼ Show ${remainingCount} more`}
        </button>
      )}
    </div>
  );
}
